import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { dateRangeOverlap } from "../../common/utils/dateUtils";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { ensureInteger } from "../../common/utils/generalUtils";

const key = r => ensureInteger(r.rosterid)

const rosterRemovalsMgrSlice = createSlice({
    name: 'rosterRemovals',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)))
            ensureArray(action.payload.data).forEach(rosterRemoval => {
                if (!state.data[key(rosterRemoval)]) {
                    state.data[key(rosterRemoval)] = rosterRemoval;
                }
                toProcess.delete(key(rosterRemoval));
            })
            toProcess.forEach(id => delete state.data[id])
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(rosterRemoval => {
                if (!state.data[key(rosterRemoval)]) {
                    state.data[key(rosterRemoval)] = rosterRemoval;
                }
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(rosterRemoval => delete state.data[key(rosterRemoval)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => rosterRemovalsMgrSlice.getInitialState())
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'rosterRemovals', action); 
const visualizeFailure = verb => DefaultFailureAction(verb, 'rosterRemovals');

const fetchRosterRemovals = aggregatingFetch(
    id => network => network.post('/api/v1/searchRosterRemovals', { id }, data => rosterRemovalsMgrSlice.set({ids: [id], data}), visualizeFailure('get')),
    ids => network => network.post('/api/v1/searchRosterRemovals', ids.map(id=> ({id})), data => rosterRemovalsMgrSlice.actions.set({ data, ids }), visualizeFailure('get'))
)
const fetchRosterRemovalsInRange = (start, end) => network => network.post('/api/v1/searchRosterRemovals', {start, end}, rosterRemovalsMgrSlice.actions.addOrUpdate, visualizeFailure)
// #endregion

// #region selectors
const selectRosterRemovalsData = state => state.rosterRemovals.data;
const selectRosterRemovalsFlattened = createSelector([selectRosterRemovalsData], rosterRemovals => Object.values(rosterRemovals))
const selectRosterRemovalsInRange = (start, end) => createSelector([selectRosterRemovalsData], rosterRemovals => {
    return Object.values(rosterRemovals).filter(rr => dateRangeOverlap({ start: rr.actual_start ?? rr.start ?? rr.shift_start, end: rr.actual_end ?? rr.end ?? rr.shift_end }, { start, end }))
})
// #endregion

const listen = {}
const cleanup = { root: rosterRemovalsMgrSlice.name, all: selectRosterRemovalsFlattened, removeAll: rosterRemovalsMgrSlice.actions.remove, key };


const rosterRemovalsInitByID = initMapFactory(fetchRosterRemovals, listen)
export const [useRosterRemovalsInRange] = dataHookFactory(selectRosterRemovalsInRange, queryMapFactory(fetchRosterRemovalsInRange, 'rosterid', rosterRemovalsInitByID, listen), { cleanup });

const reducer = { name: rosterRemovalsMgrSlice.name, reducer: rosterRemovalsMgrSlice.reducer }
export default reducer;